import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { getInstance } from '../../../../services';
import { validateApiResponse } from '../../../../shared/utils/apiResponse';
import { mapApiInstanceToUi } from '../../utils';

const isNotFound = (errOrRes) => {
  const status =
    errOrRes?.status_code ?? errOrRes?.response?.status ?? errOrRes?.response?.data?.status_code;
  return status === 404;
};

/** Instance Detail data (CT-4188). Race/unmount-safe; tabs read via useOutletContext(). */
export function useInstance(providerSlug, instanceId) {
  const { t } = useTranslation('admin-studio');
  const [instance, setInstance] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const fetchIdRef = useRef(0);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    const fetchId = ++fetchIdRef.current;
    setIsLoading(true);
    setError(null);
    setNotFound(false);
    try {
      const res = await getInstance(providerSlug, instanceId);
      if (!mountedRef.current || fetchId !== fetchIdRef.current) return;
      if (isNotFound(res)) {
        setNotFound(true);
        setInstance(null);
        return;
      }
      const body = validateApiResponse(res, t('common.requestFailed')).body;
      setInstance(mapApiInstanceToUi(body));
    } catch (err) {
      if (!mountedRef.current || fetchId !== fetchIdRef.current) return;
      if (isNotFound(err)) {
        setNotFound(true);
        setInstance(null);
        setError(null);
        return;
      }
      setError(err?.message ?? t('common.requestFailed'));
      setInstance(null);
    } finally {
      if (mountedRef.current && fetchId === fetchIdRef.current) setIsLoading(false);
    }
  }, [providerSlug, instanceId, t]);

  useEffect(() => {
    load();
  }, [load]);

  return useMemo(
    () => ({ instance, isLoading, error, notFound, refetch: load }),
    [instance, isLoading, error, notFound, load],
  );
}
